import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, AutomationInterface, Context, WorkflowStepInterface } from '@vendasta/automata';
import { combineLatest, Observable, of } from 'rxjs';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import { AutomationsRouterService } from '../../../../shared/automations-router.service';
import { map, switchMap, take } from 'rxjs/operators';

import { AutomataContextService } from '@galaxy/automata/shared';
import { CampaignSelectorService } from '../../../../data-services/campaign-selector.service';

@Injectable({
  providedIn: 'root',
})
export class StartCampaignForCompanyService extends AutomationDisplayService {
  constructor(
    protected readonly translateService: TranslateService,
    private readonly router: AutomationsRouterService,
    private readonly automationContextService: AutomataContextService,
    protected readonly routerService: AutomationsRouterService,
    private readonly campaignsService: CampaignSelectorService,
  ) {
    super();
  }

  /** Gets text for the activity column in the table. */
  getActivityColumn(
    namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): ActivityTableEntry {
    // Check for problems creating the tag.
    const workflowStepData = activity.workflowStep?.data ? JSON.parse(activity.workflowStep.data) : {};
    const inputData = activity.inputData ? JSON.parse(activity.inputData) : {};

    if (activity?.error) {
      return this.tableEntry(
        this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.START_CAMPAIGN_FOR_COMPANY.ACTIVITY.ERROR'),
        this.emptyBody(),
      );
    }
    return this.tableEntry(
      combineLatest([
        this.router.applyRoutePrefix(`/crm/company/profile/${inputData.company_id}`),
        this.router.applyRoutePrefix(`/marketing/campaign/details/${workflowStepData.campaign_id}`),
      ]).pipe(
        take(1),
        map(([linkToCompany, linkToCampaign]) =>
          this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.START_CAMPAIGN_FOR_COMPANY.ACTIVITY.SUCCESS', {
            companyUrl: linkToCompany,
            campaignUrl: linkToCampaign,
          }),
        ),
      ),
      this.getDefaultStepColumnBody(namespace, activity.workflowStep!, automation),
    );
  }

  /** The text in an automation node (i.e. the nodes in the graph). */
  getAutomationNodeData(
    _namespace: string,
    _step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const data = _step ? JSON.parse(_step?.data || '') : {};

    const campaignUrl = this.createCampaignPath(data.campaign_id);

    return combineLatest([
      this.campaignsService.get(data?.campaign_id),
      this.routerService.applyRoutePrefix(campaignUrl),
    ]).pipe(
      switchMap(([campaign, url]) => {
        if (campaign && url) {
          return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.START_CAMPAIGN_FOR_COMPANY.ACTIONV2', {
            campaignId: campaign.id,
            campaignName: campaign.name,
            campaignUrl: url,
          });
        } else {
          return of('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.ACTIVITY_DEFAULT');
        }
      }),
      switchMap((text) => {
        return this.nodeDisplayData(of(text), this.noValidationErrorsDetected());
      }),
    );
  }

  private createCampaignPath(campaignId: string): string {
    const currentContext = this.automationContextService.currentContext;
    return currentContext === Context.AUTOMATION_CONTEXT_SMB
      ? `/campaigns/${campaignId}/details`
      : `marketing/campaign/details/${campaignId}`;
  }
}
